// Flow2 inline Goals table (FF GOALS_VIEW_REVAMP_FE_AEX-3428). Renders the "Add Goal Type"
// tiles + an EditableTable of goals with inline create/edit/save/discard/delete. New goals are
// added as an editable synthetic row at the TOP of the table; the table shows goals newest-first.
import { useCallback, useMemo, useState } from 'react';
import { Box, styled, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, useStalePlanRefresh } from '../../common/hooks';
import dayjs from 'dayjs';
import { Icon, Typography } from '../../components/atoms';
import { EditableTable } from '../../components/molecules/EditableTable';
import { ConfirmationCentered } from '../../components/molecules/ConfirmationDialog/ConfirmationDialog';
import { PdfDownloadErrorBanner } from '../../components/molecules/PdfDownloadErrorBanner/PdfDownloadErrorBanner';
import { StalePlanBanner } from '../../components/molecules/StalePlanBanner/StalePlanBanner';
import { Banner } from '../../components/molecules/Banner/Banner';
import { initializePlanState } from '../../features/global/globalSlice';
import { selectEwsRequests, selectEwsResponses } from '../../features/ews';
import {
    setGoalEdit,
    setGoalEditingRowKey,
    setGoalSaveErrorType,
    setHasUnsavedGoalEdits,
    clearGoalEdit
} from '../../features/global/goalEditSlice';
import { checkFFEnabled, getClientJourneyFlowType } from '../../utils';
import { FEATURE_NAMES } from '../../common/constants';
import { GoalTypeTiles } from './GoalTypeTiles';
import { useGoalColumns } from './goalColumns';
import { MappingGuideLink } from './MappingGuideLink';
import {
    buildNewRowDefaults,
    computeGoalTenure,
    deleteInlineGoal,
    DUPLICATE_GOAL_NAME_ERROR,
    GoalInlineRow,
    GoalRowType,
    isGoalTenureLocked,
    mapPersistedGoalToRow,
    rowHasPastGoalDate,
    saveInlineGoal,
    selectIsPlanSavedOnce
} from './goalInlineHelpers';

const NEW_ROW_ID = '__new_goal_row__';

const HeaderLabel = styled(Typography)(({ theme }) => ({
    // '&&' lifts specificity above the Typography variant so the Figma 14px/19px wins
    '&&': {
        fontSize: '14px',
        lineHeight: '19px'
    },
    color: theme.palette.semantic.text.default,
    display: 'block',
    marginBottom: '12px'
}));

const EmptyCard = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: `1px solid ${theme.palette.semantic.bd.default}`,
    borderRadius: '8px',
    padding: '40px 24px',
    backgroundColor: theme.palette.neutral.white
}));

const EmptyIcon = styled('div')(({ theme }) => ({
    borderRadius: '100px',
    background: theme.palette.semantic.bg.brandLight1,
    height: '56px',
    width: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    '& > svg': { color: theme.palette.semantic.icon.brand }
}));

const TilesWrap = styled('div')(() => ({ width: '100%', marginTop: '16px', textAlign: 'center' }));

const BannerContiner = styled('div')(() => ({
    marginBottom: '8px',
    "& div[type='warning'], & div[type='error']": {
        height: '40px'
    }
}));

export const GoalsInlineTable = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const ewsRequestsState = useAppSelector(selectEwsRequests);
    const ewsResponsesState = useAppSelector(selectEwsResponses);
    const orgInfo = useAppSelector((state) => state.global.orgInfo);
    const language = useAppSelector((state) => state?.global?.currentLanguage);
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const isPlanSavedOnce = useAppSelector(selectIsPlanSavedOnce);

    // Transient inline-edit state lives in redux (goalEdit slice) so it survives the Goals-step
    // remount that the nav-lock subscription triggers in ClientJourneyFlow2. See goalEditSlice.
    const editingKey = useAppSelector((state) => state.goalEdit?.editingRowKey ?? null);
    const addType = useAppSelector((state) => state.goalEdit?.addGoalType ?? null) as GoalRowType | null;
    const saveErrorType = useAppSelector((state) => state.goalEdit?.saveErrorType ?? null);
    const saveError = saveErrorType === 'generic';
    const [duplicateNameError, setDuplicateNameError] = useState('');
    const [pendingDeleteGoalId, setPendingDeleteGoalId] = useState<string | null>(null);

    const setSaveError = useCallback(
        (value: boolean) => {
            dispatch(setGoalSaveErrorType(value ? 'generic' : null));
        },
        [dispatch]
    );
    const setIsStalePlan = useCallback(
        (value: boolean) => {
            dispatch(setGoalSaveErrorType(value ? 'stale' : null));
        },
        [dispatch]
    );

    // AEX-3363: on "Refresh Plan", close the failed edit row so the advisor restarts on fresh data.
    const handlePlanRefreshed = useCallback(() => {
        dispatch(clearGoalEdit());
        setDuplicateNameError('');
    }, [dispatch]);
    const { isStalePlan, markStaleFromError, refreshPlan } = useStalePlanRefresh(handlePlanRefreshed, {
        isStalePlan: saveErrorType === 'stale',
        setIsStalePlan
    });

    // Persisted goals mapped to rows, newest first (formFields appends newest at the end).
    const persistedRows: GoalInlineRow[] = useMemo(() => {
        const formFields = adviceGoals?.formFields ?? [];
        return [...formFields].reverse().map((ff) => mapPersistedGoalToRow(ff, adviceClient));
    }, [adviceGoals?.formFields, adviceClient]);

    const rows: GoalInlineRow[] = useMemo(() => {
        if (!addType) return persistedRows;
        const newRow: GoalInlineRow = { ...buildNewRowDefaults(addType), goalId: NEW_ROW_ID };
        return [newRow, ...persistedRows];
    }, [addType, persistedRows]);

    const hasGoals = persistedRows.length > 0;
    const isRowOpen = editingKey !== null;
    const showEmptyCard = !hasGoals && !addType;
    // AEX-3430: a reopened plan whose goal timeline dates have elapsed. The page banner and the
    // continue CTA (owned by ClientJourneyFlow2) gate on this; each affected row shows a red bar.
    const hasPastGoalDates = persistedRows.some(rowHasPastGoalDate);

    const startAdd = useCallback(
        (type: GoalRowType) => {
            if (isRowOpen) return;
            setDuplicateNameError('');
            dispatch(setGoalEdit({ editingRowKey: NEW_ROW_ID, addGoalType: type }));
        },
        [isRowOpen, dispatch]
    );

    const handleEditingKeyChange = useCallback(
        (key: string | number | null) => {
            dispatch(setGoalEditingRowKey(key === null ? null : String(key)));
            if (key === null) {
                setDuplicateNameError('');
            }
        },
        [dispatch]
    );

    // Mirror the Profile/Accounts tables: only an open row with actual (dirty) changes marks the
    // Goals step dirty, so navigating away surfaces the Stay/Leave confirmation (not on a bare edit).
    const handleDirtyStateChange = useCallback(
        (dirty: boolean) => {
            dispatch(setHasUnsavedGoalEdits(dirty));
        },
        [dispatch]
    );

    const columns = useGoalColumns({
        duplicateNameError,
        onClearDuplicate: () => setDuplicateNameError('')
    });

    // On a completed plan (step 5), editing the start/end date of a goal that is currently active
    // (started in the past, ends in the future) recomputes projections, so confirm before applying.
    const dateChangeConfirm = useMemo(
        () => ({
            watchFields: ['incomeStartDate', 'planStartRetirement', 'targetDate'],
            shouldConfirm: (row: GoalInlineRow) => {
                if (!isPlanSavedOnce) return false;
                const start =
                    row.type === 'income'
                        ? row.incomeStartDate
                        : row.type === 'retirement'
                        ? row.planStartRetirement
                        : null;
                const end = row.targetDate;
                if (!start || !end) return false;
                const now = dayjs();
                return dayjs(start).isBefore(now, 'day') && dayjs(end).isAfter(now, 'day');
            },
            title: t('GOAL_UPDATE_CONFIRM_TITLE'),
            content: <Trans t={t} i18nKey="GOAL_DATE_CHANGE_CONFIRM_CONTENT" components={{ br: <br /> }} />,
            confirmLabel: t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT'),
            cancelLabel: t('BUTTON_CANCEL')
        }),
        [isPlanSavedOnce, t]
    );

    const onSaveRow = useCallback(
        async (values: GoalInlineRow) => {
            const isEdit = values.goalId !== NEW_ROW_ID;
            setDuplicateNameError('');
            setSaveError(false);
            try {
                // AEX: a saved (tenure-locked) goal keeps its term with no generategoalcategory call;
                // only a new/unsaved goal is (re)categorized.
                const tenure = isGoalTenureLocked(values, adviceGoals)
                    ? { goalCategory: values.goalCategory ?? null, goalCategoryOrder: values.goalCategoryOrder ?? null }
                    : await computeGoalTenure(values.type, values, language);
                await saveInlineGoal(values, isEdit, tenure, {
                    dispatch,
                    adviceGoals,
                    adviceClient,
                    orgInfo,
                    isFlow2,
                    language,
                    ewsRequests: ewsRequestsState,
                    ewsResponses: ewsResponsesState
                });
            } catch (error) {
                if (error instanceof Error && error.message === DUPLICATE_GOAL_NAME_ERROR) {
                    setDuplicateNameError(t('DUPLICATE_NAME'));
                } else if (!markStaleFromError(error)) {
                    setSaveError(true);
                }
                throw error; // keep the row in edit mode
            }
        },
        [
            dispatch,
            adviceGoals,
            adviceClient,
            orgInfo,
            isFlow2,
            language,
            ewsRequestsState,
            ewsResponsesState,
            t,
            markStaleFromError,
            setSaveError
        ]
    );

    return (
        <Box sx={{ width: '100%' }}>
            {showEmptyCard ? (
                <EmptyCard>
                    <EmptyIcon>
                        {/* 37 (not 32): the target sprite has ~14% viewBox padding, so this renders the glyph at Figma's 32px */}
                        <Icon id="target_rounded_md" size={37} />
                    </EmptyIcon>
                    <Typography
                        variant="headers.h3"
                        label={t('GOALS_INLINE_EMPTY_TITLE')}
                        sx={{
                            fontSize: '18px',
                            fontWeight: 400,
                            lineHeight: '25px',
                            color: theme.palette.semantic.text.default
                        }}
                    />
                    <Typography
                        variant="body.large"
                        sx={{ fontWeight: 400, lineHeight: '22px', color: theme.palette.semantic.text.muted }}
                        label={t('GOALS_INLINE_EMPTY_SUBTITLE')}
                    />
                    <TilesWrap>
                        <GoalTypeTiles onSelect={startAdd} />
                    </TilesWrap>
                </EmptyCard>
            ) : (
                <>
                    <HeaderLabel variant="body.largeBold" label={t('ADD_GOAL_TYPE')} />
                    <Box sx={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
                        <GoalTypeTiles onSelect={startAdd} disabled={isRowOpen} />
                        <MappingGuideLink />
                    </Box>
                    <BannerContiner>
                        {saveError ? (
                            <PdfDownloadErrorBanner i18nKey="SAVE_PLAN_FAILED_BANNER" />
                        ) : isStalePlan ? (
                            <StalePlanBanner onRefresh={refreshPlan} />
                        ) : (
                            hasPastGoalDates &&
                            !isPlanSavedOnce && (
                                <Box sx={{ marginBottom: '16px' }}>
                                    <Banner
                                        type="error"
                                        messageNode={
                                            <>
                                                <b>{t('GOAL_DATE_PAST_ERROR_BANNER_PREFIX')}</b>
                                                {t('GOAL_DATE_PAST_ERROR_BANNER_MSG')}
                                            </>
                                        }
                                    />
                                </Box>
                            )
                        )}
                    </BannerContiner>
                    <Box
                        sx={{
                            '& .MuiInputBase-input': { fontWeight: 400, lineHeight: '22px' }
                        }}
                    >
                        <EditableTable<GoalInlineRow>
                            rows={rows}
                            columns={columns}
                            rowKey="goalId"
                            isAddingNew={false}
                            bodyCellVerticalAlign="top"
                            editingRowKey={editingKey}
                            onEditingRowKeyChange={handleEditingKeyChange}
                            onDirtyStateChange={handleDirtyStateChange}
                            actionsColumnLabel={t('GOALS_INLINE_COL_ACTIONS')}
                            onSaveRow={onSaveRow}
                            onDeleteRow={(row) => setPendingDeleteGoalId(row.goalId)}
                            canDeleteRow={(row) => row.goalId !== NEW_ROW_ID}
                            rowError={!isPlanSavedOnce ? rowHasPastGoalDate : undefined}
                            saveError={saveError}
                            onClearSaveError={() => setSaveError(false)}
                            skipSaveIfUnchanged
                            confirmFieldChange={dateChangeConfirm}
                            cancelConfirm={{
                                title: t('DRAWER_CLOSE_MODAL_TITLE'),
                                content: t('DRAWER_CLOSE_MODAL_HEADER_TEXT'),
                                stayLabel: t('DRAWER_CLOSE_MODAL_HEADER_TEXT_STAY'),
                                leaveLabel: t('DRAWER_CLOSE_MODAL_HEADER_TEXT_LEAVE')
                            }}
                            ariaLabels={{
                                edit: t('BUTTON_EDIT'),
                                delete: t('DELETE_TEXT'),
                                save: t('BUTTON_SAVE'),
                                cancel: t('BUTTON_CANCEL')
                            }}
                        />
                    </Box>
                </>
            )}

            {pendingDeleteGoalId && (
                <ConfirmationCentered
                    open
                    title={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_GOAL_TITLE_FTDS'
                            : 'DELETE_GOAL_TITLE'
                    )}
                    content={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_GOAL_CONTENT_FTDS'
                            : 'DELETE_GOAL_CONTENT'
                    )}
                    confirmButtonLabel={t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}
                    cancelButtonLabel={t('BUTTON_CANCEL')}
                    onConfirmation={() => {
                        deleteInlineGoal(
                            pendingDeleteGoalId,
                            adviceGoals,
                            dispatch,
                            ewsRequestsState,
                            ewsResponsesState
                        );
                        dispatch(initializePlanState({}));
                        setPendingDeleteGoalId(null);
                    }}
                    onCancellation={() => setPendingDeleteGoalId(null)}
                    onCloseIconClick={() => setPendingDeleteGoalId(null)}
                />
            )}
        </Box>
    );
};

export default GoalsInlineTable;
